import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useAllProductionAreas } from '../../hooks/useCompanyData';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';
import { Plus, Pencil, ToggleLeft, ToggleRight, MapPin } from 'lucide-react';
import './AdminRoomsPage.css';

const ROOM_TYPES = ['production', 'storage', 'qa', 'office', 'other'];

const ROOM_TYPE_BADGE = {
  production: 'badge-production',
  storage: 'badge-completed',
  qa: 'badge-submitted',
  office: 'badge-archived',
  other: 'badge-other',
};

export default function AdminRoomsPage() {
  const { t } = useTranslation();
  const { areas, loading } = useAllProductionAreas();
  const { companyId } = useAuth();
  const toast = useToast();

  const [showForm, setShowForm] = useState(false);
  const [editRoom, setEditRoom] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'production' });
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [errors, setErrors] = useState({});

  const sortedAreas = useMemo(() => {
    return [...areas].sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [areas]);

  const activeCount = useMemo(() => areas.filter((r) => r.isActive).length, [areas]);

  const openAdd = () => {
    setEditRoom(null);
    setForm({ name: '', type: 'production' });
    setErrors({});
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditRoom(r);
    setForm({ name: r.name, type: r.type || 'production' });
    setErrors({});
    setShowForm(true);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t('validation.required');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editRoom) {
        await updateDoc(doc(db, COLLECTIONS.PRODUCTION_AREAS, editRoom.id), {
          name: form.name.trim(),
          type: form.type,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, COLLECTIONS.PRODUCTION_AREAS), {
          companyId,
          name: form.name.trim(),
          type: form.type,
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      toast(t(editRoom ? 'toast.roomUpdated' : 'toast.roomSaved'), 'success');
      setShowForm(false);
    } catch {
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (room) => {
    setTogglingId(room.id);
    try {
      await updateDoc(doc(db, COLLECTIONS.PRODUCTION_AREAS, room.id), {
        isActive: !room.isActive,
        updatedAt: serverTimestamp(),
      });
      toast(
        t(room.isActive ? 'toast.roomDeactivated' : 'toast.roomActivated'),
        room.isActive ? 'warning' : 'success'
      );
    } catch {
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{t('admin.roomManagement')}</h1>
          <p className="page-header-sub">
            <MapPin size={13} /> Rooms and areas shown to production users during task entry
          </p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <Plus size={16} />
          {t('rooms.addRoom')}
        </button>
      </div>

      <div className="page-content-wide">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Rooms &amp; Areas</span>
            {!loading && (
              <span className="text-sm text-muted">
                {activeCount} active · {areas.length} total
              </span>
            )}
          </div>

          {loading ? (
            <div className="page-loading" style={{ minHeight: 200 }}>
              <div className="spinner" />
            </div>
          ) : areas.length === 0 ? (
            <div className="empty-state" style={{ padding: 'var(--space-12) var(--space-4)' }}>
              <MapPin size={32} style={{ color: 'var(--color-muted)', marginBottom: 12 }} />
              <p className="text-muted">No rooms yet — add one to get started.</p>
              <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={openAdd}>
                <Plus size={16} /> Add your first room
              </button>
            </div>
          ) : (
            <div className="table-container" style={{ borderRadius: 0, border: 'none' }}>
              <table className="table rooms-table">
                <thead>
                  <tr>
                    <th className="col-name">{t('rooms.roomName')}</th>
                    <th className="col-type">{t('rooms.roomType')}</th>
                    <th className="col-active">{t('rooms.isActive')}</th>
                    <th className="col-actions">{t('taskTable.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAreas.map((room) => (
                    <tr key={room.id} className={!room.isActive ? 'row-inactive' : ''}>
                      <td className="col-name">
                        <span className="font-semibold">{room.name}</span>
                        {!room.isActive && (
                          <span className="text-xs text-muted" style={{ marginLeft: 8 }}>
                            (inactive)
                          </span>
                        )}
                      </td>
                      <td className="col-type">
                        <span className={`badge ${ROOM_TYPE_BADGE[room.type] || 'badge-other'}`}>
                          {t(`roomsExtra.types.${room.type}`, { defaultValue: room.type })}
                        </span>
                      </td>
                      <td className="col-active">
                        <button
                          className="btn btn-icon"
                          onClick={() => handleToggleActive(room)}
                          disabled={togglingId === room.id}
                          title={room.isActive ? t('roomsExtra.deactivateTitle') : t('roomsExtra.activateTitle')}
                          aria-label={room.isActive ? t('roomsExtra.deactivateTitle') : t('roomsExtra.activateTitle')}
                        >
                          {togglingId === room.id ? (
                            <span className="spinner" style={{ width: 18, height: 18 }} />
                          ) : room.isActive ? (
                            <ToggleRight size={22} style={{ color: 'var(--color-brand)' }} />
                          ) : (
                            <ToggleLeft size={22} style={{ color: 'var(--text-muted)' }} />
                          )}
                        </button>
                      </td>
                      <td className="col-actions">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => openEdit(room)}
                          title={t('roomsExtra.editTitle')}
                        >
                          <Pencil size={14} />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <Modal
          title={editRoom ? t('rooms.editRoom') : t('rooms.addRoom')}
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button
                className="btn btn-secondary"
                onClick={() => setShowForm(false)}
                disabled={saving}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <span className="spinner" /> : t('common.save')}
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">
              {t('rooms.roomName')} <span className="required">*</span>
            </label>
            <input
              className={`form-control ${errors.name ? 'is-error' : ''}`}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              placeholder={t('roomsExtra.namePlaceholder')}
              autoFocus
            />
            {errors.name && <p className="form-error">{errors.name}</p>}
          </div>
          <div className="form-group">
            <label className="form-label">{t('rooms.roomType')}</label>
            <select
              className="form-control"
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            >
              {ROOM_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`roomsExtra.types.${type}`)}
                </option>
              ))}
            </select>
            <p className="form-hint">
              Used for grouping and filtering on the admin dashboard
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}
